<template>
    <button :type="type" :disabled="disabled" :class="buttonClasses" @click="handleClick" :aria-label="ariaLabel"
        :aria-describedby="ariaDescribedby">
        <span v-if="loading" class="button-spinner" aria-hidden="true"></span>
        <span :class="{ 'opacity-0': loading }">
            <slot />
        </span>
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface ButtonProps {
    variant?: 'primary' | 'secondary' | 'accent' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    fullWidth?: boolean
    ariaLabel?: string
    ariaDescribedby?: string
}

const props = withDefaults(defineProps<ButtonProps>(), {
    variant: 'primary',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    fullWidth: false
})

const emit = defineEmits<{
    click: [event: MouseEvent]
}>()

const buttonClasses = computed(() => {
    const baseClasses = [
        'inline-flex',
        'items-center',
        'justify-center',
        'font-medium',
        'border',
        'border-transparent',
        'rounded-lg',
        'transition-all',
        'duration-200',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-offset-2',
        'disabled:opacity-50',
        'disabled:cursor-not-allowed',
        'relative'
    ]

    // Size classes with mobile-friendly touch targets
    const sizeClasses = {
        sm: ['px-3', 'py-2', 'text-sm', 'min-h-[40px]'],
        md: ['px-4', 'py-3', 'text-base', 'min-h-[44px]', 'sm:text-base', 'text-[16px]'], // 16px prevents zoom on iOS
        lg: ['px-6', 'py-4', 'text-lg', 'min-h-[48px]']
    }

    // Variant classes
    const variantClasses = {
        primary: [
            'bg-primary-500',
            'text-white',
            'hover:bg-primary-600',
            'focus:ring-primary-500',
            'active:bg-primary-700'
        ],
        secondary: [
            'bg-gray-100',
            'text-gray-700',
            'hover:bg-gray-200',
            'focus:ring-gray-400',
            'active:bg-gray-300'
        ],
        accent: [
            'bg-secondary-500',
            'text-white',
            'hover:bg-secondary-600',
            'focus:ring-secondary-500',
            'active:bg-secondary-700'
        ],
        danger: [
            'bg-red-600',
            'text-white',
            'hover:bg-red-700',
            'focus:ring-red-500',
            'active:bg-red-800'
        ]
    }

    const widthClasses = props.fullWidth ? ['w-full'] : []

    return [
        ...baseClasses,
        ...sizeClasses[props.size],
        ...variantClasses[props.variant],
        ...widthClasses
    ]
})

const handleClick = (event: MouseEvent) => {
    if (!props.disabled && !props.loading) {
        emit('click', event)
    }
}
</script>

<style scoped>
.button-spinner {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.button-spinner::after {
    content: '';
    width: 1rem;
    height: 1rem;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Custom CSS for Tailwind classes that might not be available */
.bg-primary-500 {
    background-color: var(--color-primary-500);
}

.hover\:bg-primary-600:hover {
    background-color: var(--color-primary-600);
}

.active\:bg-primary-700:active {
    background-color: var(--color-primary-700);
}

.focus\:ring-primary-500:focus {
    --tw-ring-color: var(--color-primary-500);
}

.bg-secondary-500 {
    background-color: var(--color-secondary-500);
}

.hover\:bg-secondary-600:hover {
    background-color: var(--color-secondary-600);
}

.active\:bg-secondary-700:active {
    background-color: var(--color-secondary-700);
}

.focus\:ring-secondary-500:focus {
    --tw-ring-color: var(--color-secondary-500);
}
</style>